import REQUEST from './Request';

/** LeadStatus enum: 0 = Interested, 1 = NotInterested, 2 = CallHimBack */
export const LeadStatus = {
  Interested: 0,
  NotInterested: 1,
  CallHimBack: 2,
};

export const LeadStatusLabels = {
  [LeadStatus.Interested]: 'Interested',
  [LeadStatus.NotInterested]: 'Not Interested',
  [LeadStatus.CallHimBack]: 'Call Him Back',
};

function parseLeadsResponse(response) {
  if (typeof response === 'string') {
    try {
      return JSON.parse(response);
    } catch {
      return response;
    }
  }
  return response;
}

/**
 * Get current user's leads (paginated). Response may be JSON string; parses to { data, totalCount, pageIndex, pageSize, pagesCount }.
 */
export const getLeads = async (params = {}) => {
  const queryParams = new URLSearchParams({
    PageIndex: String(params.pageIndex ?? 1),
    PageSize: String(params.pageSize ?? 10),
    ...(params.search && { Search: params.search }),
  });
  const response = await REQUEST({
    method: 'GET',
    url: `Leads?${queryParams.toString()}`,
  });
  const parsed = parseLeadsResponse(response);
  return {
    data: parsed?.data ?? parsed?.Data ?? [],
    totalCount: parsed?.totalCount ?? parsed?.TotalCount ?? 0,
    pageIndex: parsed?.pageIndex ?? parsed?.PageIndex ?? 1,
    pageSize: parsed?.pageSize ?? parsed?.PageSize ?? 10,
    pagesCount: parsed?.pagesCount ?? parsed?.PagesCount ?? 0,
  };
};

/**
 * Check if a client already exists and whether they belong to another sales agent.
 * Returns { exists, isCurrentUserLead, isOtherSalesLead }
 */
export const checkClient = async ({ email, phoneNumber } = {}) => {
  const params = new URLSearchParams();
  if (email) params.append('email', email);
  if (phoneNumber) params.append('phoneNumber', phoneNumber);
  const response = await REQUEST({
    method: 'GET',
    url: `Leads/check-client?${params.toString()}`,
  });
  if (typeof response === 'string') {
    try { return JSON.parse(response); } catch { return response; }
  }
  return response;
};

/**
 * Add a single lead. Body: { name, email, phoneNumber?, address?, dateOfBirth?, occupation?, monthlyIncome?, note?, leadStatus?, salesId }
 */
export const addLead = async (body) => {
  return await REQUEST({
    method: 'POST',
    url: 'Leads',
    data: body,
  });
};

/**
 * Update lead note and/or leadStatus. Body: { note?, leadStatus? }
 */
export const updateLead = async (leadUserId, body) => {
  return await REQUEST({
    method: 'PUT',
    url: `Leads/${leadUserId}`,
    data: body,
  });
};

/**
 * Delete (unassign) a single lead.
 */
export const deleteLead = async (leadUserId) => {
  return await REQUEST({
    method: 'DELETE',
    url: `Leads/${leadUserId}`,
  });
};

/**
 * Add multiple leads. Body: { leads: Array<{ name, email, salesId, ... }> }
 */
export const addLeadsList = async (body) => {
  return await REQUEST({
    method: 'POST',
    url: 'Leads/list',
    data: body,
  });
};

/**
 * Delete multiple leads. Body: { leadUserIds: string[] }
 */
export const deleteLeadsList = async (leadUserIds) => {
  return await REQUEST({
    method: 'POST',
    url: 'Leads/delete-list',
    data: { leadUserIds },
  });
};

/**
 * Assign leads to a sales user. Body: { salesId, leadUserIds }
 */
export const assignLeads = async (salesId, leadUserIds) => {
  return await REQUEST({
    method: 'POST',
    url: 'Leads/assign',
    data: { salesId, leadUserIds },
  });
};

/**
 * Upload Excel file for bulk import. formData: { file, salesId? }
 */
export const uploadLeadsExcel = async (formData) => {
  return await REQUEST({
    method: 'POST',
    url: 'Leads/upload-excel',
    data: formData,
  });
};
